import { useState, useEffect, useRef } from 'react';
import { playCardSnap } from '../../utilities/sounds';
import { useTableGeometryCtx } from './useTableGeometry';
import './CommunityCards.css';

const suitSymbols = { h: '♥', d: '♦', c: '♣', s: '♠' };
const suitColors  = { h: '#f0245e', d: '#f0245e', c: '#191e4e', s: '#191e4e' };

// Gap between each seat's card flipping up, in seat-number order.
const REVEAL_INTERVAL_MS = 500;
// How long the winning card stays highlighted before the cards clear off the felt.
// The server holds slightly longer before dealing, so the table is briefly empty
// between the draw ending and the first round being dealt.
const WINNER_HOLD_MS = 1200;

// Opening "high card for the button" draw: one face-up card in front of each active
// seat, revealed 0.5s apart in seat order. The highest card (winnerSeat) is highlighted
// once the server resolves it; the whole display is cleared by the GAME_DEAL that
// follows (see socketMiddleware), so this component just renders the current draw state.
const DealerDraw = ({ draw }) => {
    const { betStyle } = useTableGeometryCtx();
    const cards = draw?.cards || null;
    const seats = cards ? Object.keys(cards).map(Number).sort((a, b) => a - b) : [];
    // Stable key for the current set of dealt cards — re-runs the reveal only on a new draw.
    const key = seats.map((s) => `${s}:${cards[s]}`).join(',');

    const [revealed, setRevealed] = useState(0);
    const [cleared, setCleared] = useState(false);
    const prevKeyRef = useRef('');

    useEffect(() => {
        if (!key) {
            setRevealed(0);
            setCleared(false);
            prevKeyRef.current = '';
            return;
        }
        if (key === prevKeyRef.current) return;
        prevKeyRef.current = key;

        setRevealed(0);
        setCleared(false);
        const timers = [];
        seats.forEach((_, i) => {
            timers.push(setTimeout(() => {
                setRevealed(i + 1);
                playCardSnap();
            }, i * REVEAL_INTERVAL_MS));
        });
        return () => timers.forEach(clearTimeout);
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

    // Once the winner is known, hold the highlight briefly then sweep the cards away,
    // so the felt is empty for a beat before the first round is dealt.
    useEffect(() => {
        if (draw?.winnerSeat == null) return;
        const t = setTimeout(() => setCleared(true), WINNER_HOLD_MS);
        return () => clearTimeout(t);
    }, [draw?.winnerSeat]);

    if (seats.length === 0 || cleared) return null;

    return (
        <>
            {seats.slice(0, revealed).map((seat) => {
                const card = cards[seat];
                const rank = card.slice(0, -1);
                const suit = card.slice(-1);
                const isWinner = draw.winnerSeat === seat;
                // Place the draw card exactly where the player's bet chips land.
                const positionStyle = betStyle(seat);
                if (!positionStyle) return null;
                return (
                    <div key={seat} className="dealer-draw-card" style={positionStyle}>
                        <div
                            className={`card card-face hole-card-dealing${isWinner ? ' dealer-draw-winner' : ''}`}
                            style={{
                                color: suitColors[suit] || '#191e4e',
                                '--deal-start-x': '0px',
                                '--deal-start-y': '-28vh',
                            }}
                        >
                            <div className="card-rank">{rank}</div>
                            <div className="card-suit">{suitSymbols[suit] || suit}</div>
                            <div className="card-rank-big">{rank}</div>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

export default DealerDraw;
